import { Injectable, OnDestroy } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@environments/environment';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { Bearertoken } from '@shared/authentication/model/bearer-token-model';
import { AuthenticationService } from './authentication.service';

/**
 * Proactively refreshes the JWT before it expires. Uses HttpBackend directly
 * (bypassing interceptors) to avoid circular dependency with ErrorInterceptor.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService implements OnDestroy {
    private rawHttp: HttpClient;
    private refreshTimer: any;
    private refreshInProgress$: Observable<boolean> | null = null;
    private userSub: Subscription;

    private readonly REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 min before expiry

    constructor(
        httpBackend: HttpBackend,
        private authService: AuthenticationService
    ) {
        this.rawHttp = new HttpClient(httpBackend);

        // Start/stop refresh timer on login/logout
        this.userSub = this.authService.user.subscribe(token => {
            if (token?.token) {
                this.scheduleRefresh(token.token);
            } else {
                this.clearTimer();
            }
        });
    }

    ngOnDestroy(): void {
        this.clearTimer();
        this.userSub?.unsubscribe();
    }

    /**
     * Refresh the token now. Safe to call from multiple places concurrently —
     * only one HTTP call is made and the result is shared.
     */
    refresh(): Observable<boolean> {
        if (this.refreshInProgress$) {
            return this.refreshInProgress$;
        }

        let userId: string;
        let currentToken: string;
        try {
            userId = this.authService.getLoggedInId();
            currentToken = this.authService.userValue?.token;
        } catch {
            return of(false);
        }

        if (!userId || !currentToken) {
            return of(false);
        }

        const headers = new HttpHeaders({
            'Authorization': `Bearer ${currentToken}`,
            'Content-Type': 'application/json'
        });

        this.refreshInProgress$ = this.rawHttp
            .post<Bearertoken>(
                `${environment.apiUrl}/refresh-token?id=${userId}`,
                '',
                { headers }
            )
            .pipe(
                tap(newToken => {
                    if (newToken?.token) {
                        // Update both localStorage AND BehaviorSubject so the
                        // interceptor immediately uses the fresh token
                        localStorage.setItem('token', JSON.stringify(newToken));
                        this.authService.userSubject.next(newToken);
                    }
                }),
                map(t => !!t?.token),
                catchError(() => of(false)),
                finalize(() => { this.refreshInProgress$ = null; }),
                shareReplay(1)
            );

        return this.refreshInProgress$;
    }

    private scheduleRefresh(token: string): void {
        this.clearTimer();

        const expMs = this.getTokenExpirationMs(token);
        if (!expMs) return;

        const delay = Math.max(expMs - Date.now() - this.REFRESH_BUFFER_MS, 0);

        this.refreshTimer = setTimeout(() => {
            this.refresh().subscribe();
        }, delay);
    }

    private getTokenExpirationMs(token: string): number | null {
        try {
            const parts = token.split('.');
            if (parts.length !== 3) return null;
            const payload = JSON.parse(atob(parts[1]));
            return payload.exp ? payload.exp * 1000 : null;
        } catch {
            return null;
        }
    }

    private clearTimer(): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
    }
}
